import type { ScheduleTakeoffRefreshPreview } from '@obraplan/core/application/contracts/schedule-from-takeoff';
import { LoadingState } from '@/components/feedback/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { hoursText, quantityText, resultadoDaRevisao } from './takeoff-preview-model';
import { motivoSemQuantitativo, type QuantitativoDoCronograma } from './use-takeoff-lines';

export interface TakeoffRefreshDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly takeoff: QuantitativoDoCronograma;
  /** `schedule.import`: sem ela, o diálogo só mostra as diferenças. */
  readonly canApply: boolean;
}

type Lugar = Pick<
  ScheduleTakeoffRefreshPreview['changed'][number],
  'towerName' | 'floorName' | 'disciplineName' | 'serviceName'
>;

function nomeDoLugar(lugar: Lugar): string {
  return `${lugar.towerName} · ${lugar.floorName} — ${lugar.disciplineName} · ${lugar.serviceName}`;
}

/**
 * As diferenças do quantitativo e o "Aplicar" (Fase 31, RN-419, D3).
 *
 * - **Com número novo:** o antes (o que o item guardou do quantitativo) e o depois, e a marca
 *   de quem tinha saído e voltou;
 * - **Entram pela geração:** o pavimento × serviço novo não vira item aqui — a geração o cria,
 *   mantendo as datas de quem já existe;
 * - **Saíram do quantitativo:** aplicar os marca, e nunca os apaga.
 *
 * Aplicar troca só a quantidade e o HH. Datas, linha de base e avanço não mudam.
 */
export function TakeoffRefreshDialog({
  open,
  onOpenChange,
  takeoff,
  canApply,
}: TakeoffRefreshDialogProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const linhas = takeoff.estado === 'PRONTO' ? takeoff.linhas : undefined;

  const resposta = useLive(
    () =>
      !open || linhas === undefined || session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.previewScheduleTakeoffRefresh.execute(
            { projectId: activeProjectId, takeoff: [...linhas] },
            session,
          ),
    [open, linhas, session?.userId, activeProjectId],
  );

  const [aplicar, aplicacao] = useCommand(
    () =>
      container.applyScheduleTakeoffRefresh.execute(
        { projectId: activeProjectId!, takeoff: [...(linhas ?? [])] },
        session!,
      ),
    { silent: true, successMessage: resultadoDaRevisao, onSuccess: () => onOpenChange(false) },
  );

  const semQuantitativo = motivoSemQuantitativo(takeoff);
  const previa = resposta !== undefined && resposta.ok ? resposta.value : undefined;
  const aAplicar = previa === undefined ? 0 : previa.changed.length + previa.gone.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>O quantitativo mudou</DialogTitle>
          <DialogDescription>
            Aplicar troca só a quantidade e o HH dos itens. As datas, a linha de base e o avanço não
            mudam.
          </DialogDescription>
        </DialogHeader>

        {semQuantitativo !== undefined ? (
          <p role="alert" className="text-sm text-(--color-state-warn)">
            {semQuantitativo}
          </p>
        ) : resposta === undefined ? (
          <LoadingState label="Comparando com o quantitativo…" />
        ) : !resposta.ok ? (
          <p role="alert" className="text-sm text-(--color-state-danger)">
            {resposta.error.message}
          </p>
        ) : (
          <div className="flex min-w-0 flex-col gap-5">
            {previa !== undefined &&
              previa.changed.length + previa.added.length + previa.gone.length === 0 && (
                <p className="text-sm text-(--color-content-muted)">
                  Nada mudou: os itens gerados já têm o número do quantitativo.
                </p>
              )}

            {previa !== undefined && previa.changed.length > 0 && (
              <section aria-label="Com número novo" className="flex min-w-0 flex-col gap-2">
                <h3 className="text-sm font-medium">
                  Com número novo ({String(previa.changed.length)})
                </h3>
                {/*
                  Uma lista com cara de tabela: no monitor, três colunas com o cabeçalho; no
                  celular, o lugar em cima e os dois números embaixo, cada um com o rótulo —
                  numa tabela rolando de lado, o antes → depois ficaria fora da tela.
                */}
                <div className="flex max-h-80 min-w-0 flex-col overflow-y-auto rounded-md border border-(--color-border-subtle)">
                  <div
                    aria-hidden="true"
                    className="hidden border-b border-(--color-border-subtle) px-3 py-2 text-xs font-medium text-(--color-content-muted) uppercase sm:grid sm:grid-cols-[minmax(0,1fr)_12rem_10rem] sm:gap-3"
                  >
                    <span>Pavimento e serviço</span>
                    <span className="text-right">Quantidade</span>
                    <span className="text-right">HH</span>
                  </div>
                  <ul className="flex flex-col">
                    {previa.changed.map((mudou) => (
                      <li
                        key={mudou.itemId}
                        data-testid="mudou"
                        className="grid min-w-0 grid-cols-1 gap-1 border-b border-(--color-border-subtle) px-3 py-2 text-sm last:border-0 sm:grid-cols-[minmax(0,1fr)_12rem_10rem] sm:items-center sm:gap-3"
                      >
                        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                          {nomeDoLugar(mudou)}
                          {mudou.returned && (
                            <Badge
                              tone="info"
                              className="whitespace-nowrap"
                              title="Estava marcado como fora do quantitativo e voltou: aplicar tira a marca"
                            >
                              voltou
                            </Badge>
                          )}
                        </span>
                        <span className="tabular sm:text-right">
                          <span className="text-(--color-content-muted) sm:sr-only">
                            Quantidade:{' '}
                          </span>
                          {quantityText(mudou.before.quantity, mudou.unit)} →{' '}
                          <strong>{quantityText(mudou.after.quantity, mudou.unit)}</strong>
                        </span>
                        <span className="tabular sm:text-right">
                          <span className="text-(--color-content-muted) sm:sr-only">HH: </span>
                          {hoursText(mudou.before.laborHours)} →{' '}
                          <strong>{hoursText(mudou.after.laborHours)}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {previa !== undefined && previa.added.length > 0 && (
              <section aria-label="Entram pela geração" className="flex min-w-0 flex-col gap-2">
                <h3 className="text-sm font-medium">
                  Entram pela geração ({String(previa.added.length)})
                </h3>
                <p className="text-xs text-(--color-content-muted)">
                  Pavimento × serviço que o quantitativo passou a ter. Aplicar não os cria: use
                  “Gerar do quantitativo”, que cria os novos e mantém as datas de quem já existe.
                </p>
                <ul className="flex flex-col gap-1 text-sm">
                  {previa.added.map((entrou) => (
                    <li
                      key={`${entrou.floorId}|${entrou.serviceId}`}
                      className="flex flex-wrap justify-between gap-x-3"
                    >
                      <span className="min-w-0">{nomeDoLugar(entrou)}</span>
                      <span className="tabular text-(--color-content-muted)">
                        {quantityText(entrou.quantity, entrou.unit)} ·{' '}
                        {hoursText(entrou.laborHours)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {previa !== undefined && previa.gone.length > 0 && (
              <section aria-label="Saíram do quantitativo" className="flex min-w-0 flex-col gap-2">
                <h3 className="text-sm font-medium">
                  Saíram do quantitativo ({String(previa.gone.length)})
                </h3>
                <p className="text-xs text-(--color-content-muted)">
                  Ficam no cronograma, com o último número, marcados como fora do quantitativo —
                  nada é apagado.
                </p>
                <ul className="flex flex-col gap-1 text-sm">
                  {previa.gone.map((saiu) => (
                    <li key={saiu.itemId} className="flex flex-wrap justify-between gap-x-3">
                      <span className="min-w-0">{nomeDoLugar(saiu)}</span>
                      <span className="tabular text-(--color-content-muted)">
                        {quantityText(saiu.quantity, saiu.unit)} · {hoursText(saiu.laborHours)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {aplicacao.error !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {aplicacao.error.message}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {canApply && previa !== undefined && aAplicar === 0 && previa.added.length > 0 && (
            <p className="order-last text-xs text-(--color-content-muted) sm:order-first sm:mr-auto sm:self-center">
              Nada a aplicar aqui: os novos entram pela geração.
            </p>
          )}
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {canApply ? 'Cancelar' : 'Fechar'}
          </Button>
          {canApply && (
            <Button
              type="button"
              variant="primary"
              disabled={previa === undefined || aAplicar === 0 || aplicacao.isPending}
              onClick={() => void aplicar()}
            >
              {aplicacao.isPending ? 'Aplicando…' : 'Aplicar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
